import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Tab, Typography } from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ProjectCommentSection } from "components/Projects/ProjectCommentSection";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { styled } from "@mui/material/styles";
import { useParams, useHistory } from "react-router-dom";

import { API_URL_PROJECTS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { makeQueryString } from "core/makeQueryString";
import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import { IProject, IProjectsResponse } from "interfaces/project";
import { Carousel } from "components/Carousel/Carousel";

import { CreateProjectDialog } from "pages/Projects/ProjectDialogs/CreateProjectDialog";
import { ProjectCasesTable } from "pages/Projects/ProjectTables/ProjectCasesTable";
import { ProjectDetail } from "pages/Projects/ProjectDetail";
import { ProjectsList } from "pages/Projects/ProjectsList";
import { ProjectsHeader } from "pages/Projects/ProjectsHeader";
import { ProjectActionsTable } from "pages/Projects/ProjectTables/ProjectActionsTable";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { useProjectQuery } from "hooks/queries/useProjectQuery";

interface Params {
  id: string;
}

const PAGE_TITLE = "Projects";

const DEFAULT_PROJECT_ORDERING = "name";
export const PROJECT_LIST_PAGE_SIZE = 25;

export const ProjectsPage = (): JSX.Element => {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);
  let { id: paramId } = useParams<Params>();
  const history = useHistory();
  const basePageUrl = "/pages/projects";

  const { axios } = useContext(AxiosContext)!;
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState("");
  const [orderingGetProjects, _] = useState<string[]>([]);

  const [selectedProject, setSelectedProject] = useState<IProject | undefined>(
    undefined
  );
  const [selectedTab, setSelectedTab] = useState<
    "actions" | "cases" | "comments"
  >("actions");

  const fetchProjects = useInfiniteQuery<IProjectsResponse, unknown>({
    queryKey: ["projects", { search: searchValue }],
    getNextPageParam: lastPage => {
      // Extract the page number from the url provided in the "next" parameter
      const matches = lastPage.next?.match(/page=[0-9]+/) ?? undefined;
      if (matches !== undefined && matches.length > 0) {
        const [_, pageNumber] = matches[0].split("=");
        return pageNumber;
      }

      // Undefined tells react query there is no next page
      return undefined;
    },
    // set the initial page to 1, as this is how the page number works on the api
    queryFn: ({ pageParam = 1 }) =>
      axios
        .get<IProjectsResponse>(
          `${API_URL_PROJECTS}${makeQueryString({
            page: pageParam,
            size: PROJECT_LIST_PAGE_SIZE,
            search: searchValue,
            ordering:
              orderingGetProjects.length > 0
                ? orderingGetProjects.join(",")
                : DEFAULT_PROJECT_ORDERING
          })}`
        )
        .then(r => r.data),
    refetchOnWindowFocus: false
  });

  const {
    data: projectData,
    isSuccess: projectSuccess,
    isLoading: projectLoading
  } = useProjectQuery(selectedProject?.id ?? Number(paramId));

  const projects = useMemo(
    () => fetchProjects.data?.pages.flatMap(page => page.results) ?? [],
    [fetchProjects.data]
  );

  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);

  const handleTabChange = (event: ChangeEvent<{}>, newValue: any): void => {
    setSelectedTab(newValue);
  };

  // Set selectedProject based on fetched projects
  useEffect(() => {
    if (!fetchProjects.isLoading && projects.length === 0) {
      setSelectedProject(undefined);
      history.replace(`${basePageUrl}`);
      return;
    }

    if (paramId && !selectedProject) {
      setSelectedProject(projectData);
      return;
    }

    if (!selectedProject && projects.length > 0) {
      const project = projects[0];
      setSelectedProject(project);
      project && history.replace(`${basePageUrl}/${project.id}`);
      return;
    }
  }, [
    paramId,
    fetchProjects.isLoading,
    projectData,
    selectedProject,
    projects,
    history
  ]);

  useEffect(() => {
    // When project is edited and projects are re-fetched, update selectedProject to reflect changes.
    const updatedProject = projects.find(
      project => project.id === Number(paramId)
    );

    if (updatedProject) {
      setSelectedProject(updatedProject);
    }
  }, [paramId, projects, projectData, history]);

  const handleLoadMoreProjects = (): void => {
    if (!fetchProjects.isFetching && fetchProjects.hasNextPage) {
      fetchProjects.fetchNextPage();
    }
  };

  return (
    <>
      <CssGrid
        sx={{
          minHeight: "calc(100vh - 100px)",
          maxHeight: "calc(100vh - 100px)"
        }}
      >
        <PaddedPaper sx={{ gridArea: "s1" }}>
          <TitleAndButtonWrapper>
            <Typography variant="h4">Projects</Typography>
            <StyledButton
              variant="contained"
              data-qa="PP-Button-Add-Project"
              color="primary"
              aria-label="Add Project button"
              onClick={() => {
                setShowCreateProjectDialog(true);
              }}
              startIcon={<AddCircle />}
            >
              Add New Project
            </StyledButton>
          </TitleAndButtonWrapper>

          <ProjectsList
            loadingInitialProjects={fetchProjects.isLoading}
            loadingMoreProjects={fetchProjects.isFetchingNextPage}
            error={fetchProjects.isError}
            projects={projects}
            selectedProject={selectedProject}
            onSelectProject={project => {
              history.replace(`${basePageUrl}/${project.id}`);
              setSelectedProject(project);
            }}
            onChangeSearchValue={setSearchValue}
            onScrolledToEnd={handleLoadMoreProjects}
          />
        </PaddedPaper>

        {selectedProject ? (
          <>
            <PaddedPaper sx={{ gridArea: "s2" }}>
              <Stack component="section">
                {projectSuccess ? (
                  <>
                    <ProjectsHeader selectedProject={projectData} />
                    <ProjectDetail selectedProject={projectData} />
                  </>
                ) : (
                  <ProgressIndicatorContainer loading={projectLoading} />
                )}
              </Stack>
            </PaddedPaper>

            <TablesPaper sx={{ gridArea: "s3" }}>
              <TabContext value={selectedTab}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <StyledTabList onChange={handleTabChange}>
                    <StyledTab label="Actions" value="actions" />
                    <StyledTab label="Cases" value="cases" />
                    <StyledTab label="Comments" value="comments" />
                  </StyledTabList>
                </Box>

                <StyledTabPanel value="actions">
                  <ActionsTabPanelContent>
                    <Box
                      sx={{
                        overflowY: { xl: "hidden" },
                        flexGrow: { xl: 1 }
                      }}
                    >
                      <ProjectActionsTable selectedProject={projectData} />
                    </Box>

                    <Box
                      sx={{
                        width: "400px",
                        overflow: { xl: "auto" }
                      }}
                    >
                      <Carousel title="Attachments" />
                    </Box>
                  </ActionsTabPanelContent>
                </StyledTabPanel>

                <StyledTabPanel value="cases">
                  <CasesTabPanelContent>
                    <ProjectCasesTable selectedProject={projectData} />
                  </CasesTabPanelContent>
                </StyledTabPanel>

                <StyledTabPanel value="comments">
                  <CommentsTabPanelContent>
                    <ProjectCommentSection projectId={selectedProject.id} />
                  </CommentsTabPanelContent>
                </StyledTabPanel>
              </TabContext>
            </TablesPaper>
          </>
        ) : (
          <NoDataTextContainer>Nothing to show</NoDataTextContainer>
        )}
      </CssGrid>

      <CreateProjectDialog
        isOpen={showCreateProjectDialog}
        onClose={() => setShowCreateProjectDialog(false)}
        onConfirm={async newProject => {
          setSelectedProject(newProject);
          setShowCreateProjectDialog(false);
          await queryClient.invalidateQueries({ queryKey: ["projects"] });
        }}
      />
    </>
  );
};

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  height: "100%",
  maxHeight: `calc(100% - ${theme.spacing(6)})`,
  padding: 0,
  paddingTop: theme.spacing(2)
}));

StyledTabPanel.displayName = "StyledTabPanel";

const TitleAndButtonWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
TitleAndButtonWrapper.displayName = "TitleAndButtonWrapper";

const StyledButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;
  width: fit-content;
`;
StyledButton.displayName = "StyledButton";

const StyledTabList = styled(TabList)`
  background-color: transparent;
`;
StyledTabList.displayName = "StyledTabList";

const StyledTab = styled(Tab)`
  color: black;
  text-transform: capitalize;
`;
StyledTab.displayName = "StyledTab";

const ActionsTabPanelContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  overflow: "auto",
  height: "100%",
  [theme.breakpoints.up("xl")]: {
    flexDirection: "row"
  }
}));
ActionsTabPanelContent.displayName = "ActionsTabPanelContent";

const CasesTabPanelContent = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
CasesTabPanelContent.displayName = "CasesTabPanelContent";

const CommentsTabPanelContent = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: auto;
`;
CommentsTabPanelContent.displayName = "CommentsTabPanel";

const NoDataTextContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  font-size: 24px;
  font-weight: 600;
`;
NoDataTextContainer.displayName = "NoDataTextContainer";

const PaddedPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(2)};
`;

PaddedPaper.displayName = "PaddedPaper";

const TablesPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(2)};
  min-width: 100%;
  min-height: 100%;
  max-height: calc(100vh - 328px);
`;
TablesPaper.displayName = "TablesPaper";

const CssGrid = styled(Box)`
  box-sizing: border-box;
  height: 100%;
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};

  ${props => props.theme.breakpoints.down("sm")} {
    grid-template-columns: auto;
    grid-template-rows: auto;
    grid-template-areas:
      "s1"
      "s2"
      "s3";
  }
  ${props => props.theme.breakpoints.up("sm")} {
    grid-template-columns: 280px 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "s1 s2"
      "s1 s3";
  }
  ${props => props.theme.breakpoints.up("md")} {
    grid-template-columns: 380px 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "s1 s2"
      "s1 s3";
  }
`;
CssGrid.displayName = "CssGrid";
